import { createSelector } from 'reselect';

import { PROJECTION } from 'constants/ApiConstants';
import type { MayBe } from 'modules/global/typing/common/common';
import { filterRequestFormatter } from 'modules/interventions/utils';
import { KioskStatus } from 'modules/kiosks/constants';
import { isDetailedKiosk } from 'modules/kiosks/guards';
import type { KioskReducer } from 'modules/kiosks/reducer';
import type { KiosksGetRequestParams, PaginatedKiosk, StoredKiosk } from 'modules/kiosks/types';
import { getCurrentUserIsAdmin, getCurrentUserIsReadOnly } from 'modules/users/selectors/currentUser';
import { getSelectedDealerId } from 'selectors/dealerSelectors';
import { getProps, getRoot, getSecondProps } from 'utils/selectors';

export type KiosksState = { kiosks: KioskReducer };

const getState = (state: KiosksState) => state.kiosks;

const getDataState = createSelector(getState, state => state.data);

export const getStatusState = createSelector(getState, state => state.status);

export const getUIState = createSelector(getState, state => state.ui);

const getKioskEntries = createSelector(getDataState, data => data.entities as Record<string, StoredKiosk>);

export const getKioskById = createSelector(
  getKioskEntries,
  getProps<MayBe<string>>,
  (entries, kioskId) => (kioskId ? entries[kioskId] : undefined)
) as unknown as <T = StoredKiosk>(state: KiosksState, kioskId: MayBe<string>) => MayBe<T>;

export const getKiosksByIds = createSelector(
  getKioskEntries,
  getProps<MayBe<string[]>>,
  (kiosks, kioskIds = []) => kioskIds.reduce<StoredKiosk[]>((acc, kioskId) => {
    if (typeof kiosks[kioskId] !== 'undefined') {
      acc.push(kiosks[kioskId]);
    }
    return acc;
  }, [])
) as unknown as <T = StoredKiosk>(state: KiosksState, kioskIds: MayBe<string[]>) => T[];

const getUIDashboard = createSelector(getUIState, ui => ui.dashboard);

const getIdsByDealerId = createSelector(
  getUIState,
  getProps<string>,
  ({ byDealerId }, dealerId) => byDealerId[dealerId] ?? []
);

const getUIDashboardData = createSelector(getUIDashboard, dashboard => dashboard.data);

export const getDashboardIds = createSelector(getUIDashboardData, data => data.ids);

export const getDashboardPage = createSelector(getUIDashboardData, data => data.page);

export const isDashboardLoading = createSelector(getUIDashboardData, data => data.loading);

export const getDashboardEntries = createSelector(
  getRoot,
  getDashboardIds,
  getKiosksByIds
) as unknown as (state: KiosksState) => PaginatedKiosk[];

export const getFilters = createSelector(getUIDashboard, dashboard => dashboard.filters);

export const getBaseRequestParams = createSelector(
  getDashboardPage,
  getSelectedDealerId,
  getFilters,
  ({ size, number }, dealerId, filters): KiosksGetRequestParams => ({
    size,
    page: number,
    projection: PROJECTION.PAGE,
    status: [KioskStatus.ACTIVE, KioskStatus.INACTIVE],
    dealerIds: dealerId ? [dealerId] : [],
    ...filterRequestFormatter(filters),
  })
);

export const deletingDashboardItem = createSelector(
  getUIDashboard,
  getProps<string>,
  (dashboard, kioskId) => dashboard.deleting.includes(kioskId)
);

export const patchingDashboardItem = createSelector(
  getUIDashboard,
  getProps<string>,
  (dashboard, kioskId) => dashboard.patching.includes(kioskId)
);

const processingActionDashboardItem = createSelector(
  deletingDashboardItem,
  patchingDashboardItem,
  (deleting, patching) => deleting || patching
);

export const canPatchOrDeleteKiosk = createSelector(
  processingActionDashboardItem,
  getCurrentUserIsAdmin,
  getCurrentUserIsReadOnly,
  (processing, admin, readonly) => admin && !readonly && !processing
);

const getUIDetails = createSelector(getUIState, ui => ui.details);

const getLoadingDetailsState = createSelector(getUIDetails, ui => ui.loading);

const creating = createSelector(getLoadingDetailsState, loading => loading.create);

const updating = createSelector(getLoadingDetailsState, loading => loading.update);

export const ready = createSelector(getUIDetails, ui => ui.ready);

export const fetching = createSelector(getLoadingDetailsState, loading => loading.fetch);

export const saving = createSelector(creating, updating, (create, update) => create || update);

export const configuring = createSelector(getLoadingDetailsState, loading => loading.configure);

const getKiosksByDealerId = createSelector(
  getRoot,
  getIdsByDealerId,
  getKiosksByIds
) as unknown as (state: KiosksState, dealerId: string) => PaginatedKiosk[];

export const getKiosksByDealerIdAndPaymentTypes = createSelector(
  getKiosksByDealerId,
  getSecondProps<string>,
  (kiosks, type) => kiosks.filter(
    kiosk => kiosk.status === KioskStatus.ACTIVE && isDetailedKiosk(kiosk) && kiosk.config?.payment?.type === type
  )
);

export const hasSomeKiosksByDealerIdAndPaymentType = createSelector(
  getKiosksByDealerIdAndPaymentTypes,
  kiosks => kiosks.length > 0
);
